/**
 * The browser's half of the session.
 *
 * Two things live here, and they are not the same thing.
 *
 * **The Identity Studio session** — an access token, a refresh token and the
 * user they belong to. Real, when the deployment is configured for IAM: the
 * access token is minted by Identity Studio, verified server-side on every call,
 * and belongs to the same human the rest of the Vaarta suite knows.
 *
 * **The upstream bearer** — a platform token forwarded to Vaarta / Data Studio
 * on data reads, kept on Vaarta's storage key so a token the platform left
 * behind (or one handed over by `?vaarta_sso=`) is still found. It predates IAM
 * and is unrelated to who may use this app.
 *
 * A deployment with no IAM configured has neither, and the app runs open — see
 * `useAuth`, which asks the backend which world it is in rather than guessing.
 */
import { appEnv } from './env';

const TOKEN_KEY = appEnv.authTokenStorageKey;
const ACCESS_KEY = 'analytics_access_token';
const REFRESH_KEY = 'analytics_refresh_token';
const USER_KEY = 'analytics_user';

export type AuthUser = {
  id: string;
  email: string;
  /** Display name when Identity Studio has one. */
  name?: string;
};

export type Session = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
};

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the session lasts for this tab only */
  }
}

function drop(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

// ── the Identity Studio session ─────────────────────────────────────────────

export function getAccessToken(): string | null {
  return read(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return read(REFRESH_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = read(USER_KEY);
  if (!raw) return null;
  try {
    const row = JSON.parse(raw) as Record<string, unknown>;
    if (typeof row.id !== 'string' || !row.id) return null;
    return {
      id: row.id,
      email: typeof row.email === 'string' ? row.email : '',
      name: typeof row.name === 'string' ? row.name : undefined,
    };
  } catch {
    return null;
  }
}

export function storeSession(session: Session): void {
  write(ACCESS_KEY, session.accessToken);
  if (session.refreshToken) write(REFRESH_KEY, session.refreshToken);
  write(USER_KEY, JSON.stringify(session.user));
}

/** Replace just the tokens, after a refresh — the user has not changed. */
export function storeTokens(accessToken: string, refreshToken: string): void {
  if (accessToken) write(ACCESS_KEY, accessToken);
  if (refreshToken) write(REFRESH_KEY, refreshToken);
}

export function clearSession(): void {
  drop(ACCESS_KEY);
  drop(REFRESH_KEY);
  drop(USER_KEY);
}

// ── the upstream bearer (platform data reads) ───────────────────────────────

export function getStoredToken(): string | null {
  return read(TOKEN_KEY);
}

export function storeToken(token: string): void {
  write(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  drop(TOKEN_KEY);
}

/** "Alice Smith" → the name; falls back to the email. */
export function displayName(user: AuthUser | null | undefined): string {
  if (!user) return '';
  return user.name?.trim() || user.email;
}
